import { useQuery } from "react-query";
import { useCurrentUser } from "../functional-components/UserContext";
import apiService from "../services/apiService";
import {
  getProductAsync,
  getProductByCockpitId,
  productIsPerSquareMeter,
  productIsPurchasable,
  productPriceUnit,
  productStockStatus
} from "../utils/ProductUtils";
import { useLang } from "./LangHooks";
import {
  useLocSettings,
  useProductTypeById,
  useSetting,
  useSettings
} from "./SettingsHooks";
import { cmsAssetUrl } from "../utils/CmsUtils";
import { enhanceProductVariants } from "../utils/CockpitUtils";
import { enhanceProductSpecifications } from "../utils/SpecsUtils";
import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import { Product } from "../types/Product";
import { ProductCategory } from "../types/ProductCategory";
import { Language } from "../config/languages";
import { useProductCategories } from "./ProductCategoryHooks";
import { useSpecsTranslation } from "./TranslationHooks";
import { useSiteFeatures } from "./useSiteFeatures";
import { useContext, useEffect, useMemo, useState } from "react";
import { CartContext } from "../functional-components/CartContext";
import useShowPricesWithVat from "./useShowPricesWithVat";
import { addVat } from "../utils/VatUtils";
import { divide } from "lodash";
import { TFunction } from "i18next";

/** Returns public products with public prices */
export function usePublicProducts(): Product[] {
  const settings = useLocSettings();
  const features = useSiteFeatures();
  const lang = useLang();
  const { data: products } = useQuery<Product[]>(
    ["products", lang],
    async (): Promise<Product[]> => {
      const products = (await apiService.loadProducts(
        lang,
        features,
        settings
      )) as Product[];
      return enhanceProductVariants(
        products.map(product => {
          return enhanceProductSpecifications(product, settings);
        }),
        settings
      );
    },
    { refetchInterval: 1000 * 60 * 15 }
  );
  return products || [];
}

/** Returns user visible products with user specific prices */
export function useUserProducts(): Product[] {
  const settings = useLocSettings();
  const features = useSiteFeatures();
  const { userSecrets } = useCurrentUser();
  const lang = useLang();
  const queryKey = userSecrets
    ? ["userproducts", lang, userSecrets]
    : ["products", lang];
  const { data: products } = useQuery<Product[]>(
    queryKey,
    async () => {
      const products = (await apiService.loadProducts(
        lang,
        features,
        settings
      )) as Product[];
      return enhanceProductVariants(
        products.map(product => {
          return enhanceProductSpecifications(product, settings);
        }),
        settings
      );
    },
    { refetchInterval: 1000 * 60 * 15 }
  );
  return products || [];
}

/** Returns public product with public prices */
export function usePublicProductByLang(
  artId: string,
  lang: Language
): Product | null {
  const settings = useSettings();
  const products = usePublicProducts();

  const { data: product } = useQuery(
    ["product", lang, artId],
    async (): Promise<Product | null> => {
      const product = await getProductAsync(products, artId, lang, false);
      if (product?.error || !product) {
        return null;
      }
      return enhanceProductSpecifications(product, settings) as Product;
    },
    { refetchInterval: 1000 * 60 * 15 }
  );

  //console.log("usePublicProduct", product);
  return product || null;
}

/** Returns public product with public prices */
export function usePublicProduct(artId: string): Product | null {
  const lang = useLang();
  return usePublicProductByLang(artId, lang);
}

/** Returns product with user specific prices */
export function useUserProduct(artId: string): Product | null {
  const settings = useSettings();
  const products = useUserProducts();
  const { userSecrets } = useCurrentUser();
  const lang = useLang();

  const withAuth = !!userSecrets;
  const queryKey = withAuth
    ? ["userproduct", lang, artId, userSecrets]
    : ["product", lang, artId];
  const { data: product } = useQuery(
    queryKey,
    async (): Promise<Product | null> => {
      const product = await getProductAsync(products, artId, lang, withAuth);
      if (product?.error || !product) {
        return null;
      }
      return enhanceProductSpecifications(product, settings);
    },
    { refetchInterval: 1000 * 60 * 15 }
  );

  //console.log("useUserProduct", product);
  return product || null;
}

export function useFittingProduct(artId: string): Product | null {
  const userProduct = useUserProduct(artId);
  const publicProduct = usePublicProduct(artId);
  const [product, setProduct] = useState(userProduct);
  useEffect(() => {
    if (userProduct?.sku) {
      setProduct({ ...userProduct });
    } else if (publicProduct?.sku) {
      setProduct({
        ...publicProduct,
        price: undefined,
        stock: undefined
      } as Product);
    } else {
      setProduct(null);
    }
  }, [userProduct, publicProduct]);
  return product;
}

export function useProductWithCockpitId(id: string): Product | undefined {
  const products = usePublicProducts();
  return getProductByCockpitId(products, id);
}

export function usePublicProductsByProductType(productType: string): Product[] {
  const settings = useSettings();
  const lang = useLang();

  const { data: products } = useQuery(
    ["productsByProductType", lang, productType],
    async (): Promise<Product[]> => {
      const products = productType
        ? ((await apiService.getPublicProductsByProductType(
            productType,
            lang
          )) as Product[])
        : [];
      return products.map(product => {
        return enhanceProductSpecifications(product, settings);
      });
    },
    { refetchInterval: 1000 * 60 * 15 }
  );
  return products || [];
}

export function useProductLabels(product?: Product) {
  const settings = useLocSettings();
  const ts = useSpecsTranslation();
  if (!product) {
    return [];
  }
  console.log("labels", settings.labels);
  return Object.keys(settings.labels)
    .map(key => {
      const labels = settings.labels[key];
      if (!Array.isArray(labels)) {
        return null;
      }
      return labels.map(label => {
        if (product.specsIndexed?.[key]?.value === label.name) {
          const labelName = ts(label.key, label.name);
          const imagePath = cmsAssetUrl(label.logo?.path);
          return { ...label, imagePath, name: labelName };
        }
        return null;
      });
    })
    .flat()
    .filter(o => o !== null);
}

/**
 * Returns all product categories without a parent
 */
export function useRootCategories(): ProductCategory[] {
  const categories = useProductCategories();
  //console.log("categories", categories);
  return cloneDeep(categories).filter(pc => !pc.parent);
}

export function useCategory(_id?: string): ProductCategory | undefined {
  const categories = useProductCategories();
  return _id ? find(categories, { _id }) : undefined;
}

export function useProductPrice(product: Product, b2b?: boolean) {
  const features = useSiteFeatures();
  const showPricesWithVat = useShowPricesWithVat(b2b);
  const isPurchasable = productIsPurchasable(product, b2b || false);
  const { unitQuantityFactor, price } = product;
  const isPerSquareMeter = productIsPerSquareMeter(unitQuantityFactor);
  const priceUnit = productPriceUnit(unitQuantityFactor);
  const { basePrice, unitPrice, previousBasePrice, currency } = price || {
    basePrice: undefined,
    unitPrice: undefined,
    previousBasePrice: undefined,
    currency: undefined
  };
  const previousPrice = previousBasePrice
    ? addVat(
        isPerSquareMeter
          ? divide(Number(previousBasePrice), unitQuantityFactor)
          : previousBasePrice
      )
    : false;
  const hasPrice = features.prices() && !!basePrice;
  const hasPreviousPrice = features.prices() && !!previousPrice;

  const unitDisplayPrice = unitPrice
    ? showPricesWithVat
      ? addVat(unitPrice)
      : unitPrice
    : 0;
  const displayPrice = basePrice
    ? showPricesWithVat
      ? addVat(basePrice)
      : basePrice
    : 0;

  return {
    isPurchasable,
    hasPrice,
    basePrice,
    unitPrice,
    priceUnit,
    isPerSquareMeter,
    hasPreviousPrice,
    previousBasePrice,
    previousPrice,
    currency,
    unitDisplayPrice,
    unitQuantityFactor,
    displayPrice
  };
}

export function useProductStockStatus(
  product: Product,
  tp: TFunction,
  b2b?: boolean
) {
  const { findItem } = useContext(CartContext);
  const inCart = findItem(product.sku);
  const stockStatus = useMemo(
    () => productStockStatus(product, inCart, tp, b2b),
    [product, inCart, tp, b2b]
  );

  return stockStatus;
}

export function useProductSpecsExtended(product: Partial<Product>) {
  const { specsIndexed, productType: productTypeId } = product;
  const productType = useProductTypeById(productTypeId);
  const hiddenSpecs = useSetting("hidden_specifications", [] as string[]);
  const coreSpecs = (
    Array.isArray(productType?.core_specifications)
      ? productType.core_specifications
      : []
  ).filter(field => !hiddenSpecs.includes(field));
  const iconSpecs = (
    Array.isArray(productType?.specifications_as_icons)
      ? productType.specifications_as_icons
      : []
  ).filter(field => !hiddenSpecs.includes(field));
  const coreSpecsIndexed = specsIndexed
    ? Object.fromEntries(
        Object.entries(specsIndexed).filter(
          ([field]) => coreSpecs.includes(field) && !iconSpecs.includes(field)
        )
      )
    : {};
  const iconSpecsIndexed = specsIndexed
    ? Object.fromEntries(
        Object.entries(specsIndexed).filter(([field]) =>
          iconSpecs.includes(field)
        )
      )
    : {};

  return { specsIndexed, coreSpecsIndexed, iconSpecsIndexed, hiddenSpecs };
}

export function useProductCartHandler(
  product: Product,
  amount?: number,
  dontExpandCart?: boolean,
  b2b?: boolean
) {
  const { loggedAddToCart, findItem } = useContext(CartContext);

  const inCart = findItem(product.sku);

  const addToCartHandler = () => {
    return loggedAddToCart(product, amount || 1, dontExpandCart, b2b);
  };

  return { inCart, addToCartHandler };
}
